/**
 * chatErrorMessage - maps a failed /api/ai-assistant response to a friendly,
 * non-technical chat message.
 *
 * Why this exists: Home.js's two chat fetch sites (sendChatMessage and
 * submitSupplierSelection) read `data.answer` after the fetch. fetch() does
 * NOT throw on a 5xx, and the backend's failure body ({"detail": "..."})
 * parses as valid JSON, so on a 500/timeout `data.answer` came back undefined
 * and the assistant bubble rendered BLANK. This helper produces the message
 * shown instead, and is shared by both call sites (DRY).
 *
 * It never surfaces the raw backend `detail` / exception text to the user
 * (information-disclosure + readability) - it only inspects it to choose
 * between timeout, temporary capacity, and generic messages.
 *
 * A 502 is not itself proof of a timeout: provider rate limits used to pass
 * through that status and were incorrectly described as "took too long".
 */

export const TIMEOUT_MESSAGE =
  'That took too long to generate. Try a shorter or more specific question.';

/**
 * Lane 1421: the question stopped at its step/size limits before any data
 * came back. Not a timeout, so it must not say "took too long". The backend
 * detail carries the marker phrase below (helpers/chat_turn_budget.py
 * BUDGET_STOP_DETAIL).
 */
export const BUDGET_STOP_MESSAGE =
  'This question needed more steps than one answer allows. Ask about part of it, for example one supplier or one category.';

const BUDGET_STOP_MARKER = 'more steps than one answer allows';

export const GENERIC_MESSAGE =
  'I encountered an error processing your request. Please try again.';

export const CAPACITY_MESSAGE =
  'The AI service is briefly at capacity. Please try again in a moment.';

/**
 * @param {{ status?: number }} response  the fetch Response (status only)
 * @param {object|null} data              best-effort parsed JSON body, or null
 * @returns {string} a user-facing chat message
 */
export function chatErrorMessage(response, data) {
  const status =
    response && typeof response.status === 'number' ? response.status : 0;
  const detail =
    data && typeof data.detail === 'string' ? data.detail.toLowerCase() : '';
  if (detail.includes(BUDGET_STOP_MARKER)) return BUDGET_STOP_MESSAGE;
  const looksLikeTimeout =
    status === 504 ||
    detail.includes('timed out') ||
    detail.includes('timeout') ||
    detail.includes('interrupted');
  if (looksLikeTimeout) return TIMEOUT_MESSAGE;
  const looksLikeCapacity =
    status === 429 ||
    status === 503 ||
    detail.includes('rate limit') ||
    detail.includes('at capacity') ||
    detail.includes('briefly busy');
  return looksLikeCapacity ? CAPACITY_MESSAGE : GENERIC_MESSAGE;
}
